import type { ReactNode } from 'react';
import React from 'react';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import CommentIcon from 'src/components/news/CommentIcon';
import TooltipButton from 'src/components/ui/TooltipButton';

import LikeDislikeIcon from 'src/components/news/LikeDislikeIcon';
import type { Props as IconProps } from 'src/components/ui/Icon';
import type { AdForSharedIcon, NewsPostForSharedIcon } from 'src/components/ui/ShareIcon';
import ShareIcon from 'src/components/ui/ShareIcon';
import ToggleItemInGallery from 'src/components/ui/ToggleItemInGallery';
import type { ToggleItemInGalleryProps } from 'src/components/ui/ToggleItemInGallery';
import type { Ad, NewsPost, PostsQuery } from 'src/types/__generated__/graphql';
import { styledComponent } from 'src/utils/styled';

const btnContainerStyle = { display: 'inline-flex' };
export interface InteractionCTAsProps {
    post?: ArrayElement<PostsQuery['posts']>;
    newsPost?: ToggleItemInGalleryProps['newsPost'] & NewsPostForSharedIcon & Pick<NewsPost, 'likedByCurrentUser'>;
    ad?: AdForSharedIcon & Pick<Ad, 'isUserFavorite'>;
    onCommentsClick?: (args: unknown) => void | Promise<void>;
    renderAfter?: ReactNode;
    iconProps?: Omit<IconProps, 'name'>;
    gap?: string;
}

const StyledBox = styledComponent(Box)<{
    $childGap?: string;
}>`
& > div {
    margin: 0 ${({ $childGap }) => $childGap ?? '0.5rem'} 0 0;
}
& div:last-child {
    margin: 0;
}
`;

const InteractionsCTAs: React.FC<InteractionCTAsProps> = ({
    post,
    newsPost,
    ad,
    onCommentsClick,
    renderAfter,
    iconProps,
    gap,
}) => {
    const { t } = useTranslation(['common', 'ad', 'news']);

    const isUserFavorite = post?.isUserFavorite ?? newsPost?.isUserFavorite ?? ad?.isUserFavorite ?? false;
    const isLikedByUser = post?.likedByCurrentUser ?? newsPost?.likedByCurrentUser ?? false;
    return (
        <StyledBox $childGap={gap} display="flex" flexDirection="row">
            <TooltipButton
                title={isUserFavorite ? t('common:tooltips.removeFromGalleries') : t('common:tooltips.addToGalleries')}
                ButtonComponent={ToggleItemInGallery}
                containerStyle={btnContainerStyle}
                buttonProps={{
                    post,
                    newsPost: newsPost as NewsPost,
                    ad,
                    iconProps,
                }}
                isControlled
            />
            {(post || newsPost) && (
                <TooltipButton
                    title={isLikedByUser ? t('common:tooltips.unlike') : t('common:tooltips.like')}
                    ButtonComponent={LikeDislikeIcon}
                    containerStyle={btnContainerStyle}
                    buttonProps={{
                        post,
                        newsPost,
                        iconProps,
                    }}
                />
            )}
            {post && (
                <TooltipButton
                    title={t('common:tooltips.comment')}
                    ButtonComponent={CommentIcon}
                    containerStyle={btnContainerStyle}
                    buttonProps={{
                        dataTargetId: 'post-comments-button',
                        ariaLabel: 'Chat',
                        onClick: onCommentsClick,
                        iconProps,
                    }}
                />
            )}
            {(ad || newsPost) && <ShareIcon newsPost={newsPost} ad={ad} color="white" iconProps={iconProps} />}
            {renderAfter}
        </StyledBox>
    );
};

export default InteractionsCTAs;
